// src/components/RepresentationList.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchChecks,
  deleteCheck,
  updateCheck,
} from "../actions/representationActions";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import Select from "./ui/Select";
import PaginationControls from "../components/PaginationControls";
import toast from "react-hot-toast";
import { formatNumber, toLatinDigits } from "../utils/numberFormat";
import { formatJalaliDate } from "../utils/dateFormat";
import { getFormErrors } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { CHECK_LABELS, validateCheck } from "../utils/recordValidation";
import {
  Search,
  Pencil,
  Trash2,
  X,
  Save,
  FileText,
  CheckCircle2,
  XCircle,
  Upload,
} from "lucide-react";

/**
 * Modernized:
 * - Removed inline-row editing; edit happens in an overlay modal
 * - Removed old CSS dependency; uses Tailwind classes
 * - RTL-friendly
 */

const RepresentationList = () => {
  const dispatch = useDispatch();
  const {
    checks = [],
    loading,
    error,
    count = 0,
    next,
    previous,
  } = useSelector((state) => state.checks);

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveErrors, setSaveErrors] = useState({ fields: {}, general: "" });

  const isPaidOptions = useMemo(
    () => [
      { value: "", label: "همه" },
      { value: "True", label: "بله" },
      { value: "False", label: "خیر" },
    ],
    []
  );

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Fetch checks
  useEffect(() => {
    dispatch(
      fetchChecks({
        page: currentPage,
        pageSize,
        search: query,
        isPaid: isPaidFilter,
      })
    );
  }, [dispatch, currentPage, pageSize, query, isPaidFilter]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const refetch = useCallback(() => {
    dispatch(
      fetchChecks({
        page: currentPage,
        pageSize,
        search: query,
        isPaid: isPaidFilter,
      })
    );
  }, [dispatch, currentPage, pageSize, query, isPaidFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm("آیا از حذف این چک اطمینان دارید؟")) return;
    try {
      await dispatch(deleteCheck(id));
      toast.success("چک حذف شد.");
      refetch();
    } catch (err) {
      toastApiError(err, {}, "حذف چک ناموفق بود.");
    }
  };

  const openEdit = (check) => {
    setSaveErrors({ fields: {}, general: "" });
    setEditingCheck(check);
    // Clone minimal safe shape. Keep document url separately.
    setEditFormData({
      id: check.id,
      check_code: check.check_code ?? "",
      issuer: check.issuer ?? "",
      value: check.value ?? "",
      issued_for: check.issued_for ?? "",
      bank: check.bank ?? "",
      date: check.date ?? "",
      is_paid: !!check.is_paid,
      document: null, // new file (if any)
    });
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    if (savingRef.current) return;
    setIsEditOpen(false);
    setEditingCheck(null);
    setEditFormData({});
    setIsSaving(false);
    refetch();
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      setEditFormData((p) => ({ ...p, [name]: files?.[0] || null }));
      return;
    }
    setEditFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : ["value", "check_code", "date"].includes(name) ? toLatinDigits(value) : value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingCheck?.id || savingRef.current) return;
    const { lines: problems, fields: problemFields } = validateCheck(editFormData);
    if (problems.length) {
      setSaveErrors({ fields: problemFields, general: "لطفاً موارد مشخص‌شده را اصلاح کنید." });
      toastErrors(problems);
      return;
    }
    savingRef.current = true;
    setSaveErrors({ fields: {}, general: "" });

    const formData = new FormData();

    // Append fields safely (DRF-friendly)
    const entries = Object.entries(editFormData);
    for (const [key, val] of entries) {
      if (key === "document") continue;

      if (val === null || typeof val === "undefined") {
        formData.append(key, "");
      } else if (typeof val === "boolean") {
        formData.append(key, val ? "true" : "false");
      } else {
        formData.append(key, String(val));
      }
    }

    if (editFormData.document instanceof File) {
      formData.append("document", editFormData.document);
    }

    try {
      setIsSaving(true);
      await dispatch(updateCheck(editingCheck.id, formData));
      savingRef.current = false;
      closeEdit();
      toast.success("تغییرات چک ذخیره شد.");
    } catch (err) {
      setSaveErrors(getFormErrors(err, Object.keys(editFormData)));
      toastApiError(err, CHECK_LABELS, "ذخیره تغییرات چک ناموفق بود.");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // react-select tailwind-ish classes without custom CSS file
  const selectClassNames = {
    control: (state) =>
      [
        "min-h-[42px] rounded-xl border px-2",
        "bg-white text-slate-900",
        "shadow-sm",
        state.isFocused
          ? "border-sky-500 ring-2 ring-sky-100"
          : "border-slate-200",
      ].join(" "),
    menu: () =>
      "rounded-xl border border-slate-200 shadow-lg overflow-hidden bg-white",
    option: (state) =>
      [
        "px-3 py-2 text-sm cursor-pointer",
        state.isFocused ? "bg-slate-100" : "bg-white",
        state.isSelected ? "bg-sky-50" : "",
      ].join(" "),
    singleValue: () => "text-sm",
    placeholder: () => "text-sm text-slate-400",
    input: () => "text-sm",
    indicatorSeparator: () => "bg-slate-200",
    dropdownIndicator: () => "text-slate-500",
    clearIndicator: () => "text-slate-500",
  };

  const rowStartIndex = (currentPage - 1) * pageSize;

  return (
    <div className="mt-24 w-full" dir="rtl">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
              لیست چک‌ها
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              مجموع: <span className="font-medium text-slate-700">{formatNumber(count)}</span>
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {/* Search */}
            <div className="relative w-full sm:w-[340px]">
              <Search
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="جستجو: کد چک / صادر کننده / در وجه"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pr-10 pl-3 text-sm text-slate-900 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            {/* Filter */}
            <div className="w-full sm:w-[200px]">
              <Select
                unstyled
                classNames={selectClassNames}
                value={
                  isPaidOptions.find((o) => o.value === isPaidFilter) ||
                  isPaidOptions[0]
                }
                onChange={(opt) => setIsPaidFilter(opt ? opt.value : "")}
                options={isPaidOptions}
                isClearable={false}
                isDisabled={loading}
                placeholder={loading ? "در حال بارگذاری..." : "همه"}
              />
            </div>
          </div>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
            در حال بارگذاری...
          </div>
        )}
        {error && !loading && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {getFormErrors({ response: { data: error } }, [], "خطا در دریافت چک‌ها").general}
          </div>
        )}

        {!loading && !error && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Top pagination */}
            <div className="border-b border-slate-200 px-3 py-3 sm:px-4">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>

            {checks.length === 0 ? (
              <div className="p-6 text-sm text-slate-600">
                هیچ چکی با این جستجو و فیلترها یافت نشد.
              </div>
            ) : (
              <>
              <div className="space-y-3 p-3 lg:hidden">
                {checks.map((check) => (
                  <article key={check.id} aria-label={`چک ${check.check_code}`} className="min-w-0 rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-bold text-slate-900">چک <bdi dir="ltr" className="break-all">{check.check_code}</bdi></h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs ${check.is_paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{check.is_paid ? "پاس شده" : "پاس نشده"}</span>
                    </div>
                    <p className="mt-3 text-lg font-bold tabular-nums text-slate-900"><bdi dir="ltr">{formatNumber(check.value, { maximumFractionDigits: 0 })}</bdi></p>
                    <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                      <dt className="text-slate-500">صادرکننده</dt><dd className="break-words">{check.issuer || "—"}</dd>
                      <dt className="text-slate-500">در وجه</dt><dd className="break-words">{check.issued_for || "—"}</dd>
                      <dt className="text-slate-500">بانک</dt><dd className="break-words">{check.bank || "—"}</dd>
                      <dt className="text-slate-500">تاریخ چک</dt><dd><bdi dir="ltr">{formatJalaliDate(check.date)}</bdi></dd>
                    </dl>
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                      <button type="button" onClick={() => openEdit(check)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"><Pencil size={16} />ویرایش</button>
                      <button type="button" onClick={() => handleDelete(check.id)} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700"><Trash2 size={16} />حذف</button>
                      {check.document ? <a href={check.document} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"><FileText size={16} />مشاهده فایل</a> : <span className="px-2 py-2 text-xs text-slate-500">بدون فایل</span>}
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden w-full overflow-auto lg:block">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-slate-700">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 text-right font-semibold">
                        ردیف
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        کد چک
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        صادر کننده
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        مبلغ
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        در وجه
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        تاریخ چک
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        بانک
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        پاس شده
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        فایل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        عملیات
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {checks.map((rep, idx) => (
                      <tr
                        key={rep.id}
                        className="border-b border-slate-100 hover:bg-slate-50/60"
                      >
                        <td className="px-3 py-3 text-slate-700">
                          {formatNumber(rowStartIndex + idx + 1)}
                        </td>
                        <td className="px-3 py-3 text-slate-900 font-medium">
                          <bdi dir="ltr">{rep.check_code}</bdi>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {rep.issuer}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <bdi dir="ltr" className="tabular-nums">{formatNumber(rep.value, { maximumFractionDigits: 0 })}</bdi>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {rep.issued_for}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-slate-700"><bdi dir="ltr">{formatJalaliDate(rep.date)}</bdi></td>
                        <td className="px-3 py-3 text-slate-700">{rep.bank}</td>
                        <td className="px-3 py-3">
                          {rep.is_paid ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
                              <CheckCircle2 size={16} />
                              بله
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">
                              <XCircle size={16} />
                              خیر
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {rep.document ? (
                            <a
                              href={rep.document}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <FileText size={16} />
                              مشاهده
                            </a>
                          ) : (
                            <span className="text-slate-400">بدون فایل</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-start gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(rep)}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil size={16} />
                              ویرایش
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(rep.id)}
                              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-700 hover:bg-rose-100"
                            >
                              <Trash2 size={16} />
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}

            {/* Bottom pagination */}
            <div className="border-t border-slate-200 px-3 py-3 sm:px-4">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditOpen && (
        <EditCheckModal
          open={isEditOpen}
          loading={isSaving}
          check={editingCheck}
          form={editFormData}
          errors={saveErrors}
          onClose={closeEdit}
          onChange={handleEditChange}
          onDateChange={(value) => {
            setEditFormData((p) => ({
              ...p,
              date: value ? toLatinDigits(value.format("YYYY-MM-DD")) : "",
            }));
          }}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
};

export default RepresentationList;

/* ------------------------------- Modal ------------------------------- */

function EditCheckModal({
  open,
  loading,
  check,
  form,
  errors,
  onClose,
  onChange,
  onDateChange,
  onSave,
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, onSave]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999]" dir="rtl">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div role="dialog" aria-modal="true" aria-labelledby="edit-check-title" aria-busy={loading} className="flex max-h-[90dvh] w-full max-w-3xl flex-col rounded-2xl border border-slate-200 bg-white shadow-xl">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-right">
              <h3 id="edit-check-title" className="text-base font-semibold text-slate-900">
                ویرایش چک
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                شناسه:{" "}
                <span className="font-medium text-slate-700">{check?.id}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="بستن"
              disabled={loading}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
            {errors.general && <div role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{errors.general}<ul className="mt-2 list-inside list-disc">{Object.entries(errors.fields).map(([key, message]) => <li key={key}>{checkFieldLabels[key] || "اطلاعات چک"}: {message}</li>)}</ul></div>}
            <fieldset disabled={loading} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="کد چک">
                <input
                  name="check_code"
                  dir="ltr"
                  inputMode="numeric"
                  value={form.check_code ?? ""}
                  onChange={onChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </Field>

              <Field label="صادر کننده">
                <input
                  name="issuer"
                  value={form.issuer || ""}
                  onChange={onChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </Field>

              <Field label="مبلغ">
                <input
                  name="value"
                  dir="ltr"
                  inputMode="numeric"
                  value={form.value ?? ""}
                  onChange={onChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </Field>

              <Field label="در وجه">
                <input
                  name="issued_for"
                  value={form.issued_for || ""}
                  onChange={onChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </Field>

              <Field label="تاریخ چک" id="edit-check-date">
                <div className="rounded-xl border border-slate-200 px-2 py-1.5 focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100">
                  <DatePicker
                    id="edit-check-date"
                    calendar={persian}
                    locale={persian_fa}
                    format="YYYY-MM-DD"
                    portal
                    zIndex={1100}
                    containerClassName="w-full"
                    value={form.date || ""}
                    onChange={onDateChange}
                    style={{
                      width: "100%",
                      border: "none",
                      boxShadow: "none",
                      outline: "none",
                      padding: "6px 4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </Field>

              <Field label="بانک">
                <input
                  name="bank"
                  value={form.bank || ""}
                  onChange={onChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </Field>

              <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-sm text-slate-700">پاس شده</div>
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_paid"
                    checked={!!form.is_paid}
                    onChange={onChange}
                    className="h-4 w-4 accent-sky-600"
                  />
                  <span className="text-sm text-slate-700">
                    {form.is_paid ? "بله" : "خیر"}
                  </span>
                </label>
              </div>

              <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-800">فایل</div>
                  {check?.document ? (
                    <a
                      href={check.document}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-sky-700 hover:underline"
                    >
                      مشاهده فایل فعلی
                    </a>
                  ) : (
                    <span className="text-sm text-slate-400">
                      فایل فعلی ندارد
                    </span>
                  )}
                </div>

                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3 hover:bg-slate-50">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <Upload size={18} />
                    {form.document instanceof File
                      ? form.document.name
                      : "انتخاب فایل جدید"}
                  </div>
                  <input
                    type="file"
                    name="document"
                    onChange={onChange}
                    className="hidden"
                  />
                </label>
              </div>
            </fieldset>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <X size={16} />
              انصراف
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700 disabled:opacity-60"
            >
              <Save size={16} />
              {loading ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="text-right">
      <label htmlFor={id || `edit-check-${children.props.name}`} className="mb-1 block text-xs font-medium text-slate-600">
        {label}
      </label>
      {children.type === "input" ? React.cloneElement(children, { id: `edit-check-${children.props.name}` }) : children}
    </div>
  );
}

const checkFieldLabels = { check_code: "کد چک", issuer: "صادرکننده", value: "مبلغ", issued_for: "در وجه", bank: "بانک", date: "تاریخ", document: "فایل پیوست", is_paid: "وضعیت" };
